import React from 'react';
import SiteLayout, { PALETTE } from '../site/SiteLayout';
import SEO from '../../components/SEO';

const SITE_URL = 'https://keystone.cloudvoro.com';

const SECTION_TITLE = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: 'clamp(22px, 2.6vw, 30px)',
  fontWeight: 700,
  color: PALETTE.navy,
  margin: '40px 0 14px',
  letterSpacing: '-0.015em',
};

const BODY = { fontSize: 15.5, lineHeight: 1.7, color: PALETTE.slate, margin: '0 0 14px' };

const SUB_PROCESSORS = [
  {
    name: 'Amazon Web Services EMEA SARL',
    address: '38 Avenue John F. Kennedy, L-1855 Luxembourg',
    purpose: 'Infrastructure hosting for Keystone application servers, MongoDB Atlas clusters and S3 object storage.',
    dataCategories: 'All tenant operational data, user account records, audit logs, encrypted backups.',
    location: 'EU, AWS Ireland (eu-west-1, Dublin)',
    transferMechanism: 'Intra-EU, no transfer mechanism required.',
    contract: 'AWS Customer Agreement + Data Processing Addendum (signed).',
  },
  {
    name: 'MongoDB, Inc. (via MongoDB Atlas)',
    address: '1633 Broadway, 38th Floor, New York, NY 10019, USA, EU operations via MongoDB Ireland Ltd.',
    purpose: 'Managed database hosting (multi-tenant cluster, per-tenant logical isolation).',
    dataCategories: 'All tenant operational data at rest.',
    location: 'EU, MongoDB Atlas eu-west-1 region (AWS Ireland).',
    transferMechanism: 'EU-hosted shard placement. SCCs in place for any transfer outside the EEA.',
    contract: 'MongoDB Cloud Subscription Agreement + Data Processing Addendum (signed).',
  },
  {
    name: 'CloudVoro SMTP relay (mail.cloudvoro.com)',
    address: 'CloudVoro infrastructure, Ireland.',
    purpose: 'Outbound transactional email: password resets, contact-form replies, audit-event notifications.',
    dataCategories: 'Recipient email address, subject, plain-text and HTML body of transactional messages.',
    location: 'EU, Ireland.',
    transferMechanism: 'Intra-EU.',
    contract: 'Internal CloudVoro infrastructure, controlled directly by CloudVoro.',
  },
  {
    name: 'Stripe Payments Europe Ltd.',
    address: '1 Grand Canal Street Lower, Grand Canal Dock, Dublin, D02 H210, Ireland.',
    purpose: 'Card payment processing for paid Keystone licences. Engaged only when a customer pays by card.',
    dataCategories: 'Cardholder data is handled directly by Stripe, CloudVoro does not see or store PAN data. We retain Stripe customer IDs, invoice IDs and payment metadata.',
    location: 'EU, Ireland (Stripe primary EU entity).',
    transferMechanism: 'Intra-EU. SCCs in place for any onward transfer to Stripe US.',
    contract: 'Stripe Services Agreement + Data Processing Agreement (signed).',
  },
  {
    name: 'Calendly LLC',
    address: '271 17th Street NW, Atlanta, GA 30363, USA.',
    purpose: 'Discovery-call scheduling on the public marketing site. Engaged only when a visitor chooses to book a call.',
    dataCategories: 'Visitor name, email, optional company name, requested call slot, optional message.',
    location: 'United States.',
    transferMechanism: 'EU Standard Contractual Clauses (Module 2) executed via Calendly DPA. No special-category data submitted.',
    contract: 'Calendly Terms of Service + Data Processing Addendum (signed).',
  },
  {
    name: 'Google LLC, Google Analytics (only when explicit opt-in)',
    address: '1600 Amphitheatre Parkway, Mountain View, CA 94043, USA.',
    purpose: 'Aggregate website analytics. NOT loaded by default. Engaged only after explicit cookie consent.',
    dataCategories: 'IP address (anonymised), user agent, page paths, referrer, visit duration.',
    location: 'United States.',
    transferMechanism: 'EU Standard Contractual Clauses + IP anonymisation. Strictly opt-in.',
    contract: 'Google Analytics Terms + Data Processing Terms.',
  },
];

export default function SubProcessors() {
  return (
    <SiteLayout>
      <SEO
        title="Sub-Processors, Keystone by CloudVoro"
        description="Named list of every sub-processor that touches Keystone customer data: AWS Ireland, MongoDB Atlas EU, Stripe Ireland, Calendly. Addresses, processing purposes, locations and transfer mechanisms, updated when sub-processors change."
        path="/legal/sub-processors"
        keywords="Keystone sub-processors, CloudVoro DPA, GDPR sub-processor list food software, EU data residency, AWS Ireland processor, MongoDB Atlas EU processor"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Sub-Processors',
          url: `${SITE_URL}/legal/sub-processors`,
          description:
            'Named list of every sub-processor that touches Keystone customer data, with addresses, processing purposes, locations and transfer mechanisms.',
        }}
      />

      <section style={{ background: PALETTE.cream, padding: '80px 32px 24px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }} data-testid="sub-processors-list">
          <div style={{
            fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
            fontWeight: 700, color: PALETTE.magenta, marginBottom: 18,
          }}>Sub-processors</div>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(36px, 5vw, 56px)',
            fontWeight: 700,
            color: PALETTE.navy,
            margin: 0,
            letterSpacing: '-0.025em',
            lineHeight: 1.05,
          }}>
            Who touches your data,<br/>
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>and where they are.</em>
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.6, color: PALETTE.slate, marginTop: 22, maxWidth: 720 }}>
            The complete list of CloudVoro's sub-processors for the Keystone product. Material changes are emailed to the primary tenant contact at least 30 days before they take effect, in line with Art. 28(2) GDPR.
          </p>
          <div style={{ fontSize: 12, color: PALETTE.muted, marginTop: 18, letterSpacing: '0.04em' }}>
            Effective May 2026 · Last reviewed May 2026 · Version 2.0
          </div>
        </div>
      </section>

      <section style={{ background: PALETTE.paper, padding: '32px 32px 96px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }}>

          <h2 style={SECTION_TITLE}>Current sub-processors</h2>
          <p style={BODY}>
            All sub-processors listed below have signed a Data Processing Agreement (or equivalent) with CloudVoro, are subject to confidentiality obligations of equivalent strength to those CloudVoro owes its own customers, and are reviewed annually as part of our vendor-risk programme.
          </p>

          {SUB_PROCESSORS.map((sp, i) => (
            <div key={sp.name} style={{
              margin: '24px 0',
              padding: '24px 28px',
              background: PALETTE.bgSoft,
              borderLeft: `3px solid ${PALETTE.magenta}`,
            }}>
              <div style={{
                fontSize: 11,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                fontWeight: 700,
                color: PALETTE.muted,
              }}>
                Sub-processor {String(i + 1).padStart(2, '0')}
              </div>
              <div style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 22,
                fontWeight: 700,
                color: PALETTE.navy,
                marginTop: 4,
                marginBottom: 14,
                lineHeight: 1.2,
              }}>{sp.name}</div>
              {[
                { k: 'Registered address', v: sp.address },
                { k: 'Processing purpose', v: sp.purpose },
                { k: 'Data categories', v: sp.dataCategories },
                { k: 'Processing location', v: sp.location },
                { k: 'Transfer mechanism', v: sp.transferMechanism },
                { k: 'Contract in force', v: sp.contract },
              ].map((r) => (
                <div key={r.k} style={{
                  display: 'grid',
                  gridTemplateColumns: '180px 1fr',
                  gap: 16,
                  padding: '8px 0',
                  borderBottom: `1px solid ${PALETTE.border}`,
                  fontSize: 13.5,
                }}>
                  <div style={{ color: PALETTE.muted, fontWeight: 700, letterSpacing: '0.04em', fontSize: 11, textTransform: 'uppercase' }}>{r.k}</div>
                  <div style={{ color: PALETTE.ink, lineHeight: 1.6 }}>{r.v}</div>
                </div>
              ))}
            </div>
          ))}

          <h2 style={SECTION_TITLE}>Notification of changes</h2>
          <p style={BODY}>
            CloudVoro will notify the primary tenant contact at least <strong style={{ color: PALETTE.navy }}>30 calendar days</strong> before adding, removing or replacing a sub-processor that handles tenant data. Customers may object on reasonable grounds within that window; if no commercially viable resolution can be reached, the customer may terminate the affected service with pro-rata refund of pre-paid fees.
          </p>

          <h2 style={SECTION_TITLE}>How to subscribe to updates</h2>
          <p style={BODY}>
            Tenant administrators are automatically subscribed using the contact email on file. To add a procurement contact or DPO at your organisation, write to{' '}
            <a href="mailto:privacy@cloudvoro.com" style={{ color: PALETTE.magenta, fontWeight: 600 }}>privacy@cloudvoro.com</a>{' '}
            and we'll add them to the notification list.
          </p>

          <h2 style={SECTION_TITLE}>Audit rights</h2>
          <p style={BODY}>
            Enterprise customers have a contractual right to audit our sub-processor arrangements once per calendar year, on 30 days' notice, on a remote-questionnaire basis. On-site audits can be arranged for cause (e.g. following a notified incident) on commercially reasonable terms.
          </p>

        </div>
      </section>
    </SiteLayout>
  );
}
